<template>
  <div>
    <h2 class="heading-2">{{ i18n(translations.title) }}</h2>
    <div class="mb-32">{{ i18n(translations.infoText) }}</div>
    <h3 class="mb-16">{{ i18n(translations.question) }}</h3>

    <RevRadio
      :id="UploadTypes.listingsOnly"
      v-model="uploadType"
      class="mb-32"
      :value="UploadTypes.listingsOnly"
      variant="default"
    >
      <template #label>
        <div class="body-1-bold">{{ i18n(translations.createListings) }}</div>
      </template>
    </RevRadio>

    <div class="mb-32">
      <RevRadio
        :id="UploadTypes.listingsWithProduct"
        v-model="uploadType"
        class="mb-16"
        :value="UploadTypes.listingsWithProduct"
        variant="default"
      >
        <template #label>
          <div class="body-1-bold">
            {{ i18n(translations.withNewItems) }}
          </div>
          <div>{{ i18n(translations.withNewItemsInfo) }}</div>
        </template>
      </RevRadio>

      <div v-if="uploadType === UploadTypes.listingsWithProduct" class="px-7">
        <div>{{ i18n(translations.chooseCategory) }}</div>

        <RevInputAutocomplete
          id="search-categories"
          v-model="searchCategoryInput"
          class="mb-16"
          :clear-button-aria-label="i18n(translations.clear)"
          :label="i18n(translations.chooseCategoryPlaceholder)"
          :options="searchCategoryInputOptions"
          type="text"
          @clear="handleClearSearchCategory"
          @select-item="handleSearchCategory"
        />

        <div>{{ i18n(translations.selectCategory) }}</div>

        <RevInputSelectSearchable
          id="select-level-two"
          v-model="selectedLevelTwoCategoryOption"
          class="mb-2 w-full"
          :clear-button-aria-label="i18n(translations.clear)"
          :label="i18n(translations.selectLevelTwoPlaceholder)"
          :no-options-message="i18n(translations.noResults)"
          :options="levelTwoOptions"
          @text-change="selectedLevelTwoCategoryChange"
        />

        <RevInputSelectSearchable
          v-if="hasSelectedLevelTwoCategoryOption"
          id="select-level-three"
          v-model="selectedLevelThreeCategoryOption"
          class="w-full"
          :clear-button-aria-label="i18n(translations.clear)"
          :label="i18n(translations.selectLevelThreePlaceholder)"
          :no-options-message="i18n(translations.noResults)"
          :options="levelThreeOptions"
        />
      </div>
    </div>

    <div
      v-if="selectedCategoryName"
      class="text-action-success-hi body-2-bold mb-16 flex items-center"
    >
      <IconCheckInCircle class="mr-8" />
      {{ i18n(translations.confirmationMessage, { selectedCategoryName }) }}
    </div>

    <RevButton
      :disabled="!canGenerateCSV"
      variant="primary"
      @click="handleGenerateCsv"
    >
      {{ i18n(translations.generateTemplate) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevInputAutocomplete } from '@ds/components/InputAutocomplete'
import { RevInputSelectSearchable } from '@ds/components/InputSelectSearchable'
import { RevRadio } from '@ds/components/Radio'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'

import {
  type TransformedCategory,
  UploadTypes,
} from '../../BatchListingCreation.types'

import { MAX_AUTO_COMPLETE_OPTIONS } from './UploadForm.constants'
import translations from './UploadForm.translations'
import type {
  RevInputAutocompleteOption,
  RevInputAutocompleteOptions,
  RevInputSelectSearchableOption,
} from './UploadForm.types'
import { toOption } from './UploadForm.utils'

const props = defineProps<{ categories: TransformedCategory[] }>()
const emit = defineEmits(['submit'])

const i18n = useI18n()

const searchCategoryInput = ref('')
const selectedSearchCategoryOption = ref<null | RevInputAutocompleteOption>(
  null,
)

const selectedLevelThreeCategoryOption =
  ref<null | RevInputSelectSearchableOption>(null)
const selectedLevelTwoCategoryOption =
  ref<null | RevInputSelectSearchableOption>(null)

const uploadType = ref<UploadTypes>(UploadTypes.listingsOnly)

const selectedCategoryName = computed(() => {
  if (selectedSearchCategoryOption.value)
    return selectedSearchCategoryOption.value.label

  if (
    selectedLevelTwoCategoryOption.value?.value &&
    selectedLevelThreeCategoryOption.value?.value
  )
    return selectedLevelThreeCategoryOption.value?.label

  return ''
})

const canGenerateCSV = computed(
  () =>
    uploadType.value === UploadTypes.listingsOnly || selectedCategoryName.value,
)

const hasSelectedLevelTwoCategoryOption = computed(
  () => selectedLevelTwoCategoryOption.value !== null,
)

const levelTwoCategories = computed(() =>
  props.categories.filter((category) => category.categoryLevel === 2),
)

const levelThreeCategories = computed(() =>
  props.categories.filter((category) => category.categoryLevel === 3),
)

const levelTwoOptions = computed(() =>
  levelTwoCategories.value.map((category) => toOption(category)),
)

const levelThreeOptions = computed(() =>
  !selectedLevelTwoCategoryOption.value?.id
    ? []
    : props.categories
        .filter(
          (category) =>
            category.parentCategoryId.toString() ===
            selectedLevelTwoCategoryOption.value?.id,
        )
        .map((category) => ({
          id: category.categoryId.toString(),
          label: category.categoryName,
          value: category.categoryId.toString(),
        })),
)

const searchCategoryInputOptions = computed(() => {
  if (!searchCategoryInput.value) return [] as RevInputAutocompleteOptions

  return levelThreeCategories.value
    .map((category) => toOption(category))
    .filter(
      (choice) =>
        choice.label
          .toLowerCase()
          .indexOf(searchCategoryInput.value.toLowerCase()) !== -1,
    )
    .slice(0, MAX_AUTO_COMPLETE_OPTIONS) as RevInputAutocompleteOptions
})

function resetSearchCategory() {
  searchCategoryInput.value = ''
  selectedSearchCategoryOption.value = null
}

function resetSelectedLevelCategories() {
  selectedLevelTwoCategoryOption.value = null
  selectedLevelThreeCategoryOption.value = null
}

function handleClearSearchCategory() {
  resetSelectedLevelCategories()
}

function handleSearchCategory(option: RevInputAutocompleteOption) {
  searchCategoryInput.value = option.label
  selectedSearchCategoryOption.value = option
  resetSelectedLevelCategories()
}

function selectedLevelTwoCategoryChange(value: string) {
  resetSearchCategory()

  if (value === '') {
    selectedLevelThreeCategoryOption.value = null
  }
}

function handleGenerateCsv() {
  if (selectedSearchCategoryOption.value?.label) {
    emit('submit', {
      uploadType: uploadType.value,
      selectedCategoryId: selectedSearchCategoryOption?.value.value,
    })
  } else if (
    selectedLevelTwoCategoryOption.value?.value &&
    selectedLevelThreeCategoryOption.value?.value
  ) {
    emit('submit', {
      uploadType: uploadType.value,
      selectedCategoryId: selectedLevelThreeCategoryOption.value.value,
    })
  } else {
    emit('submit', {
      uploadType: uploadType.value,
      selectedCategoryId: '',
    })
  }
}
</script>
